/**
 * Assinatura de erro da API, do ponto de vista do frontend.
 *
 * O middleware responde todo não-2xx com o envelope:
 *
 *   { "error": { "status": number, "message": string, "code"?: string,
 *                "errors"?: { campo: string[] } } }
 *
 * Este módulo define o tipo que o app manipula depois de normalizar esse
 * envelope. Nenhum componente deve ler o corpo bruto da resposta.
 */

export type ApiErrorCode =
  // emitidos pelo middleware
  | 'CALLER_URL_MISSING'
  | 'RECAPTCHA_TOKEN_MISSING'
  | 'RECAPTCHA_INVALID'
  | 'RECAPTCHA_ACTION_MISMATCH'
  | 'RECAPTCHA_LOW_SCORE'
  | 'RECAPTCHA_ERROR'
  | 'UPSTREAM_UNAVAILABLE'
  | 'MIDDLEWARE_ERROR'
  // emitidos pelo próprio front
  | 'BUSINESS_RULE' // HTTP 200 com retorno:false — regra de negócio recusou
  | 'NETWORK' // o navegador não alcançou o middleware

export interface ApiErrorParams {
  status: number
  message: string
  code?: ApiErrorCode
  fields?: Record<string, string[]>
  retryAfter?: number
  requestId?: string
  usedFallback?: boolean
  raw?: unknown
}

export class ApiError extends Error {
  /** HTTP real da resposta. 0 quando não houve resposta. */
  readonly status: number
  /** Causa conhecida, quando o middleware ou o front conseguem nomeá-la. */
  readonly code?: ApiErrorCode
  /** Mapa de campos inválidos do 422, já normalizado pelo middleware. */
  readonly fields?: Record<string, string[]>
  /** Segundos do header Retry-After, quando presente (429/503). */
  readonly retryAfter?: number
  /** Id de correlação com o log do middleware (header x-request-id). */
  readonly requestId?: string
  /**
   * true quando `message` veio da tabela de fallback do front, não da API.
   * O componente pode então trocar por um texto traduzido; se for false, a
   * mensagem é da API e já vem no idioma pedido via Accept-Language.
   */
  readonly usedFallback: boolean
  /** Corpo original, só para log. */
  readonly raw?: unknown

  constructor({
    status,
    message,
    code,
    fields,
    retryAfter,
    requestId,
    usedFallback = false,
    raw
  }: ApiErrorParams) {
    super(message)
    // target es5: sem isto, `instanceof ApiError` retorna false.
    Object.setPrototypeOf(this, ApiError.prototype)

    this.name = 'ApiError'
    this.status = status
    this.code = code
    this.fields = fields
    this.retryAfter = retryAfter
    this.requestId = requestId
    this.usedFallback = usedFallback
    this.raw = raw
  }
}

export function isApiError(valor: unknown): valor is ApiError {
  return valor instanceof ApiError
}

/**
 * Resultado discriminado, usado pelos endpoints de VALIDAÇÃO — aqueles em que
 * falhar é resposta esperada, não exceção. Endpoints que executam uma
 * transação (compra, ativação, pagamento) lançam ApiError.
 */
export type ApiResult<T> =
  | { ok: true; data: T }
  | { ok: false; error: ApiError }
